"use client";

import { useEffect, useRef, useState } from "react";

import {
  ProviderGlyph,
  GoogleG,
  FacebookF,
  InstagramMark,
} from "@/components/auth/SocialIcons";
import {
  useAuth,
  type AuthProvider as AuthProviderId,
} from "@/context/AuthProvider";

/**
 * Menu di registrazione e profilo nell'header del sito.
 *
 *   · Se l'utente NON è registrato: pulsante petrolio "Accedi" che
 *     apre un popover con la registrazione (3 provider social + form
 *     email). Stesso flusso del desk legale, ma agganciato all'header
 *     del sito così uno può registrarsi anche senza passare per un
 *     servizio specifico.
 *   · Se l'utente è registrato: pillola compatta col glifo del
 *     provider + primo nome. Cliccandola si apre un mini-menu con
 *     nome completo, email, provider e bottone "Esci".
 */

const PROVIDER_LABEL: Record<AuthProviderId, string> = {
  email: "Email",
  google: "Google",
  facebook: "Facebook",
  instagram: "Instagram",
};

function firstName(full: string): string {
  const t = full.trim();
  if (!t) return "Utente";
  return t.split(/\s+/)[0];
}

/** `suScuro`: la versione che sta nella fascia delle sezioni, che ha
 *  toni suoi. Dal 01/10/2026 il bottone "Accedi" porta un fondo suo, il
 *  petrolio, e ha lo stesso vestito dappertutto: la variante cambia
 *  solo il contorno del fuoco, che segue quello delle voci. La
 *  pillola di chi è già entrato ha già un fondo suo, e il pannello di
 *  registrazione si apre sopra la pagina, non sulla fascia: tutti e
 *  due restano come sono. */
/** Il vestito del bottone «Accedi», condiviso dal bottone vero e dal suo
 *  gemello invisibile: la riserva di spazio deve essere larga e alta
 *  esattamente quanto quello che arriva. Misure di `.login-action` in
 *  Report_proposta_OI.html (consegna del 01/10/2026): fondo petrolio,
 *  filetto sottile, sans 11/1,4 a peso normale, maiuscolo con 0,7 pixel
 *  di spaziatura, padding 8×14, altezza minima 34, raggio 2. Sul
 *  telefono l'altezza minima sale a 44, la misura di un bersaglio da
 *  toccare: là il bottone sta nella riga del marchio, che è alta 44
 *  comunque, quindi la testata non cresce.
 *
 *  `LARGHEZZA_MENU` è la stessa per «Accedi», per il suo gemello
 *  invisibile e per la pillola di chi è entrato. Il gemello teneva il
 *  posto di «Accedi», ma la pillola (segno, nome, freccia) è più larga:
 *  da autenticati, all'idratazione, il margine di servizio scivolava di
 *  diciotto pixel su ogni rotta (CLS della testata 0,0001, misurato). Con
 *  una misura sola i tre stati occupano la stessa scatola e la testata non
 *  si muove; il nome che non ci sta finisce coi puntini, quello intero è
 *  nel menu che la pillola apre. */
const LARGHEZZA_MENU = "w-[92px]";
const VESTITO_ACCEDI =
  "inline-flex min-h-[34px] max-md:min-h-[44px] items-center justify-center rounded-[2px] border bg-petrolio px-[14px] py-2 font-sans text-[11px] leading-[1.4] font-normal uppercase tracking-[0.7px]";

/** Dove si aprono i due pannelli (registrazione e profilo).
 *
 *  Da `md` in su sotto il bottone, allineati al suo bordo destro. Sul
 *  telefono no: il bottone sta a metà della riga, e un pannello largo 300
 *  allineato a destra del bottone usciva dal bordo sinistro dello schermo
 *  (a 390 cominciava a −30). Là il contenitore del menu resta `static`,
 *  quindi il pannello si aggancia al primo antenato posizionato, che è
 *  la `<header>` (`relative`): si apre sotto tutta la testata, largo
 *  quanto la finestra meno sedici pixel per lato, a qualunque larghezza
 *  di telefono. Sotto `lg` la fascia delle sezioni non c'è, quindi il
 *  fondo della testata è il fondo della riga del marchio. */
const AGGANCIO = "md:relative";
const PANNELLO =
  "max-md:inset-x-4 max-md:top-full max-md:w-auto md:right-0 md:top-full md:mt-1";

/** «Accedi» nella riga alta dei disegni del 15/10/2026: è un `.oi-link`,
 *  cioè Geist 14 a peso 600 nel mare, con il filetto al piede che compare
 *  al passaggio, e padding 12×8. Nessun fondo e nessun bordo. */
const VESTITO_ACCEDI_TESTATA =
  "inline-flex items-center border-0 border-b border-b-transparent bg-transparent px-2 py-3 font-sans text-[14px] leading-[18px] font-semibold tracking-normal text-mare transition-colors hover:border-b-current";

export function AuthMenu({
  suScuro = false,
  testata = false,
}: {
  suScuro?: boolean;
  testata?: boolean;
}) {
  const { user, ready, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // chiudi al click esterno
  useEffect(() => {
    if (!open) return;
    const onClick = (ev: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // chiudi su Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  /* Aperto da fuori: il «Leggi il dispaccio» della pagina del
     dispaccio (components/article/DispaccioView.tsx) manda `oi:apri-accesso` invece di
     portare altrove, così si accede dallo stesso pannello di sempre. Le
     istanze del menu sono due (riga del telefono e fascia da `lg`) e una
     delle due è nascosta: si apre solo quella che ha una scatola a schermo.
     Il fuoco va sul bottone, perché il pannello è nel suo stesso
     contenitore e la tastiera riparte da lì. */
  useEffect(() => {
    const apri = () => {
      const el = wrapRef.current;
      if (!el || el.getClientRects().length === 0) return;
      setOpen(true);
      el.querySelector("button")?.focus({ preventScroll: true });
    };
    window.addEventListener("oi:apri-accesso", apri);
    return () => window.removeEventListener("oi:apri-accesso", apri);
  }, []);

  /* Finché non si sa chi è, il menu tiene il suo posto invece di non
     esserci.

     `ready` diventa vero quando <AuthProvider> ha letto localStorage,
     cioè dopo l'idratazione: prima di allora questo componente tornava
     null e nell'HTML del server il bottone non c'era affatto. Compariva
     un secondo e mezzo dopo il caricamento (misura del dev server: sul
     build di produzione sono ~300 ms dopo il primo paint, i numeri
     stanno nel commento in testa a context/AuthProvider.tsx), largo 80
     pixel, e spingeva
     di lato tutto quello che ha accanto nell'intestazione: misurato a
     390, il gruppo a destra passava da 158 a 245 pixel e il bottone del
     tema si spostava di 86.

     Il segnaposto è il bottone stesso reso invisibile, non un rettangolo
     con una misura scritta a mano: così è largo esattamente quanto
     quello che arriva, in qualunque carattere e a qualunque corpo.
     Invisibile e non un finto "Accedi" visibile, perché a chi è già
     registrato comparirebbe per un istante un invito ad accedere.
     `visibility: hidden` toglie l'elemento anche ai lettori di schermo,
     quindi non serve altro. */
  if (!ready) {
    return (
      <span
        aria-hidden
        className={`invisible ${testata ? VESTITO_ACCEDI_TESTATA : `${VESTITO_ACCEDI} ${LARGHEZZA_MENU}`}`}
      >
        Accedi
      </span>
    );
  }

  // ===== LOGGED OUT: pulsante "Accedi" + popover registrazione =====
  if (!user) {
    return (
      <div ref={wrapRef} className={`inline-flex items-center ${AGGANCIO}`}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          /* Il contorno del fuoco è scritto a mano come nella testata:
             quello di serie è un grigio al 50% e sulla fascia non si
             vede.

             Il pulsante è petrolio, discreto, accanto al cambio tema: è la
             consegna del 01/10/2026 (`.login-action` del riferimento,
             pannello petrolio col testo pieno, sabbia al passaggio). Lo
             stesso vestito sulla fascia e nella riga del telefono, e
             niente più l'oro scritto a mano (#a8742a), che non seguiva il
             tema. Misure del riferimento (`.login-action`, stili calcolati):
             sans 11 a peso normale, maiuscolo spaziato 0,7 pixel,
             padding 8×14, altezza minima 34, raggio 2, e niente stellina
             davanti (VESTITO_ACCEDI qui sopra). Contrasti del testo sul petrolio: 11,12:1
             nel chiaro, 12,37:1 nello scuro; il sabbia del passaggio 5,09 e
             6,40. Aperto il pannello resta acceso in sabbia: dice che il
             riquadro sotto è suo. */
          className={testata ? `${VESTITO_ACCEDI_TESTATA} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent` : `${VESTITO_ACCEDI} ${LARGHEZZA_MENU} transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${
            suScuro
              ? "focus-visible:outline-[var(--color-navbarGlow)]"
              : "focus-visible:outline-accent"
          } ${
            open
              ? "border-sabbiaTesto text-sabbiaTesto"
              : "border-filettoPetrolio text-ink hover:border-sabbiaTesto hover:text-sabbiaTesto"
          }`}
        >
          Accedi
        </button>
        {open ? (
          <RegistrationPopover
            onRegister={(name, email, provider) => {
              login({ name, email, provider });
              setOpen(false);
            }}
          />
        ) : null}
      </div>
    );
  }

  // ===== LOGGED IN: pillola utente + menu =====
  return (
    <div ref={wrapRef} className={`inline-flex items-center ${AGGANCIO}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`inline-flex min-h-[34px] max-md:min-h-[44px] items-center justify-center gap-1.5 rounded-sm border border-accent/60 bg-surface px-2 py-1 ${LARGHEZZA_MENU} text-[10px] font-bold uppercase tracking-etichetta text-ink transition-colors hover:bg-accentSoft`}
      >
        <ProviderGlyph
          provider={user.provider}
          size={14}
          gradientId="oi-pill-ig"
        />
        <span className="min-w-0 truncate">{firstName(user.name)}</span>
        <span aria-hidden className="text-[8px] text-muted">
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className={`absolute z-50 w-[260px] rounded-sm border border-rule bg-surface p-3 shadow-[0_10px_28px_rgba(0,0,0,0.18)] ${PANNELLO}`}
        >
          <div className="mb-2 flex items-center gap-2">
            <ProviderGlyph
              provider={user.provider}
              size={22}
              gradientId="oi-menu-ig"
            />
            <div className="min-w-0">
              <div className="truncate text-[13px] font-bold text-ink">
                {user.name}
              </div>
              <div className="truncate font-mono text-[10.5px] text-muted">
                {user.email}
              </div>
            </div>
          </div>
          <div className="mb-2 font-mono text-[9.5px] uppercase tracking-[0.18em] text-muted">
            Registrato via {PROVIDER_LABEL[user.provider]}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="inline-flex w-full items-center justify-center rounded-sm border border-rule bg-bg/40 px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:border-accent hover:text-accent"
          >
            Esci
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ================================================================
   Popover di registrazione (visibile quando NON sei loggato)
   ================================================================ */

function RegistrationPopover({
  onRegister,
}: {
  onRegister: (name: string, email: string, provider: AuthProviderId) => void;
}) {
  const [n, setN] = useState("");
  const [e, setE] = useState("");

  function social(p: "google" | "facebook" | "instagram") {
    const mocks: Record<typeof p, { name: string; email: string }> = {
      google: { name: "Mario Rossi", email: "mario.rossi@gmail.com" },
      facebook: { name: "Mario Rossi", email: "mario.rossi@facebook.com" },
      instagram: { name: "Mario Rossi", email: "mariorossi@instagram.com" },
    };
    onRegister(mocks[p].name, mocks[p].email, p);
  }

  function submitEmail() {
    if (!n.trim() || !e.trim()) return;
    onRegister(n.trim(), e.trim(), "email");
  }

  return (
    <div
      role="menu"
      className={`absolute z-50 w-[300px] rounded-sm border-2 border-accent bg-surface p-4 shadow-[0_10px_28px_rgba(0,0,0,0.22)] ${PANNELLO}`}
    >
      <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-etichetta text-accent">
        Osservatorio Italiano · Registrazione
      </div>
      <h3 className="m-0 mb-3 text-[15px] font-bold leading-[1.2] text-ink">
        Entra nel tuo profilo.
      </h3>
      <p className="m-0 mb-3 text-[12.5px] leading-[1.45] text-muted">
        Registrandoti hai accesso ai desk operativi dei servizi, senza
        dover ripetere la registrazione ogni volta.
      </p>

      <div className="grid grid-cols-3 gap-2">
        <SocialBtn label="Google" onClick={() => social("google")}>
          <GoogleG size={18} />
        </SocialBtn>
        <SocialBtn label="Facebook" onClick={() => social("facebook")}>
          <FacebookF size={18} />
        </SocialBtn>
        <SocialBtn
          label="Instagram"
          onClick={() => social("instagram")}
        >
          <InstagramMark size={18} gradientId="oi-popover-ig" />
        </SocialBtn>
      </div>

      <div className="my-3 flex items-center gap-2">
        <span aria-hidden className="h-px flex-1 bg-rule" />
        <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-muted">
          oppure email
        </span>
        <span aria-hidden className="h-px flex-1 bg-rule" />
      </div>

      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={n}
          onChange={(ev) => setN(ev.target.value)}
          placeholder="Nome e cognome"
          className="w-full rounded-sm border border-rule bg-bg/30 px-3 py-2 text-[13.5px] text-ink outline-none focus:border-accent"
        />
        <input
          type="email"
          value={e}
          onChange={(ev) => setE(ev.target.value)}
          placeholder="email@esempio.it"
          className="w-full rounded-sm border border-rule bg-bg/30 px-3 py-2 text-[13.5px] text-ink outline-none focus:border-accent"
        />
        <button
          type="button"
          onClick={submitEmail}
          disabled={!n.trim() || !e.trim()}
          className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-accent px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-onAccent transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          Entra
          <span aria-hidden className="font-mono text-[14px] leading-none">
            →
          </span>
        </button>
      </div>
    </div>
  );
}

function SocialBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Registrati con ${label}`}
      title={`Registrati con ${label}`}
      className="inline-flex flex-col items-center justify-center gap-1 rounded-sm border border-rule bg-surface px-2 py-2.5 transition-colors hover:border-accent hover:bg-bg/30"
    >
      {children}
      <span className="text-[10px] font-bold uppercase tracking-etichetta text-ink">
        {label}
      </span>
    </button>
  );
}
